import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as cssLinter from "css";
import parse from "html-react-parser";
import uniqueId from "lodash/uniqueId";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    mainTitle = "mainTitle",
    infoDesc = "infoDesc",
    gridImage = "gridImage",
    imageAligment = "imageAligment",
    cssClass = "cssClass",
    contentTitle = "contentTitle",
    imageLink = "imageLink",
    contentDesc = "contentDesc",
    addContactDetails = "addContactDetails",
    addLinks = "addLinks",
    hours = "hours",
    email = "email",
    phone = "phone",
    links = "links",
    brandImage = "brandImage",
    brandDates = "brandDates",
    openInNewWidow = "openInNewWidow",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonTextColor = "buttonTextColor",
    customCSS = "customCSS",
}

interface LinkInterface {
    label?: string;
    link: LinkFieldValue;
    openInNewWindow?: boolean;
    variant?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
}

interface LinksModel {
    fields: LinkInterface;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.mainTitle]: string;
        [fields.contentTitle]: string;
        [fields.infoDesc]: string;
        [fields.contentDesc]: string;
        [fields.gridImage]: string;
        [fields.imageAligment]: string;
        [fields.customCSS]: string;
        [fields.addContactDetails]: boolean;
        [fields.addLinks]: boolean;
        [fields.hours]: string;
        [fields.email]: string;
        [fields.phone]: number;
        [fields.phone]: number;
        [fields.links]: LinksModel[];
        [fields.buttonBackgroundColor]: string;
        [fields.buttonTextColor]: string;
        [fields.brandImage]: string;
        [fields.brandDates]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.openInNewWidow]: boolean;
    };
}

type Props = OwnProps;

export interface SurImageGridStyles {
    container?: InjectableCss;
    infoTitleContainer?: InjectableCss;
    contactInfoContainer?: InjectableCss;
    mainTitle?: TypographyProps;
    desc?: TypographyProps;
    imageGridItemStyles?: GridItemProps;
    contentGridStyles?: GridItemProps;
    contentTitleStyles?: TypographyProps;
    contentDescStyles?: TypographyProps;
    contactInfo?: TypographyProps;
    cta?: LinkProps;
    linksCta?: LinkProps;
    linksContainer?: InjectableCss;
    fullWidth?: InjectableCss;
    goldenCTA?: LinkProps;
    normalCTA?: LinkProps;
    brandImgStyles?: InjectableCss;
    brandDateStyles?: InjectableCss;
}
export const imageGridStyles: SurImageGridStyles = {
    container: {
        css: css`
            &:first-child {
                margin-top: 0;
            }
            margin: 30px 0 0;
            &:last-child {
                margin-bottom: 50px;
            }
            > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                justify-content: center;
                padding: 30px 0;
                margin: 0 !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                        &:last-child {
                            margin-bottom: 30px;
                        }

                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 100% !important;
                            padding: 15px 20px;
                        }
                    `,
                    css`
                        margin: 0;
                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 100% !important;
                            padding: 15px 20px;
                        }
                    `,
                    css`
                        margin: 0;
                        > div[class^="GridWrapper"] > div[class^="GridOffset"] {
                            width: 90% !important;
                            margin: 0 auto !important;
                            padding: 30px 0;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    infoTitleContainer: {
        css: css`
            margin-bottom: 36px;
        `,
    },
    mainTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 6px;
            /* text-transform: uppercase; */
        `,
    },
    desc: {
        variant: "p",
        css: css`
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            font-size: ${TYPOGRAPHY.size.small};
            text-align: center;
        `,
    },
    fullWidth: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    contentTitleStyles: {
        variant: "h3",
        css: css`
            margin-bottom: 30px;
            width: 100%;
            /* text-transform: capitalize; */
            color: ${getColor("text.main")} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 20px;
                        font-size: 20px;
                    `,
                    css`
                        margin-bottom: 20px;
                        font-size: 20px;
                    `,
                    css`
                        margin-bottom: 20px;
                        font-size: 22px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contentDescStyles: {
        variant: "body",
        css: css`
            margin-bottom: 28px;
            width: 100%;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            p {
                margin-bottom: 0 !important;
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
    contactInfo: {
        variant: "p",
        css: css`
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            strong {
                margin-right: 5px;
            }
            margin-bottom: 2px;
        `,
    },
    contactInfoContainer: {
        css: css`
            width: 100%;
        `,
    },
    cta: {
        color: COLORS.darkmagenta,
        css: css`
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};

            .email-cta {
                text-decoration: underline;
            }
        `,
    },
    linksContainer: {
        css: css`
            margin-top: 20px;
            width: 100%;
        `,
    },
    brandImgStyles: {
        css: css`
            max-width: 200px;
            max-height: 81px;
            margin-bottom: 18px;
        `,
    },
    brandDateStyles: {
        css: css`
            width: 200px;
            height: 40px;
            background: ${getColor("primary")};
            color: white;
            margin-bottom: 18px;
            font-weight: 700;
            font-size: 15px;
            line-height: 22px;
            padding: 8px 15px;
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = imageGridStyles;

const defaultCustomCss = `

.imgGridItem{

}

.mainImgGridItem{

}

.brandImage{
    
}

`;

const CustomLinkWrapper = styled.div<{
    variant?: string;
}>`
    a {
        outline: 0;
        background-color: ${props => (props.variant === "golden" ? COLORS.golden : COLORS.white)};
        border: 1px solid ${props => (props.variant === "golden" ? COLORS.golden : COLORS.darkmagenta)};
        height: 44px;
        padding: 10px 34px;
        min-width: 356px;
        max-width: 100%;
        display: block;
        margin-bottom: 20px;
        span {
            display: block;
            text-align: center;
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            font-weight: ${TYPOGRAPHY.weight.bold};
        }
    }
`;

const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);
const phoneRegexp = /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const SurImgGridContent: React.FC<Props> = ({ fields }) => {
    const ctaLinks = useGetLinks(fields.links, o => o.fields.link);
    const { url } = useGetLink(fields.imageLink);

    const ImageGridStyles: GridItemProps = {
        width: [12, 12, 12, 6, 6],
        css: css`
            max-width: 426px !important;
            padding: 0 !important;
            order: ${fields.imageAligment === "Left" ? 0 : 1};
            img {
                width: 100%;
                object-fit: cover;
                aspect-ratio: 2/1;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: none !important;
                        order: 0 !important;
                        img {
                            aspect-ratio: 1/1;
                            height: auto;
                        }
                    `,
                    css`
                        max-width: none !important;
                        order: 0 !important;
                        img {
                            aspect-ratio: 1/1;
                            height: auto;
                        }
                    `,
                    css`
                        flex-basis: 42%;
                        max-width: 42%;
                    `,
                    null,
                    null,
                ])}
        `,
    };

    const ContentGridStyles: GridItemProps = {
        width: [12, 12, 12, 6, 6],
        align: "middle",
        css: css`
            order: ${fields.imageAligment === "Left" ? 1 : 0};
            padding-left: ${fields.imageAligment === "Left" ? "70px" : 0};
            padding-right: ${fields.imageAligment === "Right" ? "70px" : 0};
            max-width: 580px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 14px !important;
                        order: 1 !important;
                        margin-top: 15px !important;
                    `,
                    css`
                        padding: 0 14px !important;
                        order: 1 !important;
                        margin-top: 15px !important;
                    `,
                    css`
                        flex-basis: 57%;
                        max-width: 57%;
                    `,
                    null,
                    null,
                ])}
        `,
    };

    const customwrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customwrapper}>
            <StyledWrapper {...styles.container}>
                {fields.mainTitle || fields.infoDesc ? (
                    <StyledWrapper {...styles.infoTitleContainer}>
                        {fields.mainTitle ? <Typography {...styles.mainTitle}>{fields.mainTitle}</Typography> : null}
                        {fields.infoDesc ? <Typography {...styles.desc}>{fields.infoDesc}</Typography> : null}
                    </StyledWrapper>
                ) : null}
                <GridContainer>
                    <GridItem {...ImageGridStyles} className="imgGridItem">
                        <Img src={fields.gridImage} altText={fields.mainTitle || "MainImage"}  className="mainImgGridItem"/>
                    </GridItem>
                    <GridItem {...ContentGridStyles} >
                        <StyledWrapper {...styles.fullWidth}>
                            {fields.brandImage ? (
                                <LazyImage src={fields.brandImage} {...styles.brandImgStyles} altText="BrandImage" className="brandImage"/>
                            ) : null}
                            {fields.brandDates ? (
                                <StyledWrapper {...styles.brandDateStyles} >{fields.brandDates}</StyledWrapper>
                            ) : null}
                            {fields.contentTitle ? (
                                <StyledWrapper>
                                    {url ? (
                                        <Link
                                            href={url}
                                            target={fields.openInNewWidow ? `_blank` : `_self`}
                                            aria-label={fields.contentTitle}
                                            
                                        >
                                            <Typography {...styles.contentTitleStyles}>
                                                {fields.contentTitle}
                                            </Typography>
                                        </Link>
                                    ) : (
                                        <Typography {...styles.contentTitleStyles} >{fields.contentTitle}</Typography>
                                    )}
                                </StyledWrapper>
                            ) : null}
                            {fields.contentDesc ? (
                                <Typography {...styles.contentDescStyles}>
                                    {parse(fields.contentDesc, parserOptions)}
                                </Typography>
                            ) : null}
                            {fields.addContactDetails ? (
                                <StyledWrapper {...styles.contactInfoContainer}>
                                    {fields.hours ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Hours:</strong>
                                            {fields.hours}
                                        </Typography>
                                    ) : null}

                                    {fields.email ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Email:</strong>
                                            {emailsRegexp.test(fields.email) ? (
                                                <Link
                                                    {...styles.cta}
                                                    href={`mailto:${fields.email}`}
                                                    aria-label={fields.email}
                                                >
                                                    <span className="email-cta">{fields.email}</span>
                                                </Link>
                                            ) : (
                                                `Invalid Email`
                                            )}
                                        </Typography>
                                    ) : null}

                                    {fields.phone ? (
                                        <Typography {...styles.contactInfo}>
                                            <strong>Phone:</strong>
                                            {phoneRegexp.test(String(fields.phone)) ? (
                                                <Link {...styles.cta} href={`tel:${fields.phone}`}>
                                                    {fields.phone}
                                                </Link>
                                            ) : (
                                                `Invalid Phone`
                                            )}
                                        </Typography>
                                    ) : null}
                                </StyledWrapper>
                            ) : null}
                            {fields.addLinks && fields.links.length ? (
                                <StyledWrapper {...styles.linksContainer}>
                                    {fields.links.map((item, index) => {
                                        const linksCta = {
                                            css: css`
                                                background-color: ${item.fields.buttonBackgroundColor} !important;
                                                border-color: ${item.fields.buttonBackgroundColor} !important;
                                                color: ${item.fields.buttonTextColor} !important;

                                                span {
                                                color: ${item.fields.buttonTextColor} !important;
                                                }
                                            `,
                                        };
                                        return (
                                            <CustomLinkWrapper key={uniqueId()} variant={item.fields.variant}>
                                                <Link
                                                    href={ctaLinks[index].url}
                                                    target={item.fields.openInNewWindow ? `_blank` : `_self`}
                                                    {...linksCta}
                                                    aria-label={item.fields.label}
                                                >
                                                    {item.fields.label}
                                                </Link>
                                            </CustomLinkWrapper>
                                        );
                                    })}
                                </StyledWrapper>

                            ) : null}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};



const SurImgGridContentWidget: WidgetModule = {
    component: SurImgGridContent,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.mainTitle,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.infoDesc,
                displayName: "Sub Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.gridImage,
                fieldType: "General",
                displayName: "Grid Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
                sortOrder: 0,
            },

            {
                name: fields.contentTitle,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 1,
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },

            {
                name: fields.contentDesc,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 2,
            },
            {
                name: fields.brandImage,
                displayName: "Brand Name",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 7,
            },
            {
                name: fields.brandDates,
                displayName: "Brand dates",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 8,
            },

            {
                name: fields.hours,
                displayName: "Hours",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                tab: contentTab,
                sortOrder: 3,
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "example@email.com",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                tab: contentTab,
                sortOrder: 4,
            },
            {
                name: fields.phone,
                displayName: "Contact Number",
                editorTemplate: "TextField",
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.addContactDetails,
                defaultValue: "",
                tab: contentTab,
                sortOrder: 5,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.label;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 6,
                isVisible: (item: HasFields) => item.fields.addLinks,
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "link",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "buttonBackgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Button Background Color",
                        defaultValue: "",
                    },
                    {
                        name: "buttonTextColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Button Text Color",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { value: "normal", displayName: "Normal" },
                            { value: "golden", displayName: "Golden" },
                        ],
                        defaultValue: "normal",
                    },
                ],
            },

            {
                name: fields.imageAligment,
                fieldType: "General",
                displayName: "Image Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { value: "Left", displayName: "Left" },
                    { value: "Right", displayName: "Right" },
                ],
                defaultValue: "Left",
                tab: settingsTab,
                sortOrder: 0,
            },
            {
                name: fields.addContactDetails,
                displayName: "Add Contact Details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.addLinks,
                displayName: "Add Links",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};
export default SurImgGridContentWidget;
